import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

export interface IssueRow {
  id: string;
  title: string;
  description?: string | null;
  storyPoints?: number | null;
  priority?: string | null;
  status: string;
  type?: string | null;
}

interface IssuePickerProps {
  projectId: string;
  sessionId: string;
  openIssues: IssueRow[];
  onIssueSelected: (issue: IssueRow, contextMessage: string) => void;
  selectedIssueId?: string;
}

const PRIORITY_ORDER: Record<string, number> = { P0: 0, P1: 1, P2: 2 };

const priorityBadgeStyle = (priority: string | null | undefined): React.CSSProperties => {
  if (priority === 'P0') return { background: 'rgba(245,158,11,0.15)', color: 'var(--warning)', border: '1px solid rgba(245,158,11,0.3)' };
  if (priority === 'P1') return { background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' };
  return { background: 'rgba(107,114,128,0.12)', color: 'var(--text-secondary)', border: '1px solid rgba(107,114,128,0.2)' };
};

export default function IssuePicker({ projectId, sessionId, openIssues, onIssueSelected, selectedIssueId: initialSelectedId }: IssuePickerProps) {
  const [priorityFilter, setPriorityFilter] = useState<string>('All');
  const [searchText, setSearchText]         = useState('');
  const [selectedId, setSelectedId]         = useState<string | undefined>(initialSelectedId);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');

  const filtered = openIssues.filter(issue => {
    const matchesPriority = priorityFilter === 'All' || issue.priority === priorityFilter;
    const matchesSearch   = issue.title.toLowerCase().includes(searchText.toLowerCase());
    return matchesPriority && matchesSearch;
  });

  const selectedIssue = openIssues.find(i => i.id === selectedId);

  const handleStart = async () => {
    if (!selectedIssue) return;
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch<{ contextMessage: string }>(`/projects/${projectId}/sessions/${sessionId}/issue`, {
        method: 'PATCH',
        body: JSON.stringify({ issueId: selectedIssue.id }),
      });
      onIssueSelected(selectedIssue, resp.contextMessage);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to select issue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-xl)',
      padding: '20px 24px',
      maxWidth: 680,
    }}>
      {/* Header */}
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
        Select an issue to work on
      </h3>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        {openIssues.length} open issue{openIssues.length !== 1 ? 's' : ''} in sprint
      </p>

      {/* Filters row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        {/* Priority filter buttons */}
        <div style={{ display: 'flex', gap: 6 }}>
          {['All', 'P0', 'P1', 'P2'].map(p => (
            <button
              key={p}
              onClick={() => setPriorityFilter(p)}
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 12,
                fontWeight: 500,
                padding: '4px 10px',
                borderRadius: 'var(--radius-sm)',
                border: priorityFilter === p ? '1px solid var(--primary)' : '1px solid var(--border)',
                background: priorityFilter === p ? 'rgba(124,58,237,0.12)' : 'transparent',
                color: priorityFilter === p ? 'var(--primary)' : 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search issues…"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 13,
            padding: '5px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            outline: 'none',
            flex: 1,
            minWidth: 140,
          }}
        />
      </div>

      {/* Issue list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16, maxHeight: 320, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          openIssues.length === 0 ? (
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
                No sprint issues found.
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Issues are created when an approved Sprint Backlog is synced.{' '}
                <Link to={`/projects/${projectId}/chat`} style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                  Generate a Sprint Backlog
                </Link>{' '}
                via the Sprint Planning Agent, then approve it in{' '}
                <Link to={`/artefacts?projectId=${projectId}`} style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                  Artefacts
                </Link>.
              </p>
            </div>
          ) : (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', padding: '12px 0' }}>
              No issues match your filter.
            </p>
          )
        ) : (
          filtered.map(issue => {
            const isSelected = selectedId === issue.id;
            const isP0 = issue.priority === 'P0';

            return (
              <div
                key={issue.id}
                onClick={() => setSelectedId(issue.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: isSelected ? '1px solid var(--primary)' : '1px solid var(--border)',
                  background: isSelected ? 'rgba(124,58,237,0.06)' : 'transparent',
                  borderLeft: isP0 ? '3px solid rgba(245,158,11,0.7)' : isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {/* Radio indicator */}
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: isSelected ? '5px solid var(--primary)' : '2px solid var(--border)',
                  flexShrink: 0,
                  transition: 'border 0.15s',
                }} />

                {/* Title */}
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 13,
                  color: 'var(--text)',
                  flex: 1,
                  fontWeight: isSelected ? 500 : 400,
                }}>
                  {issue.title}
                </span>

                {/* Priority badge */}
                {issue.priority && (
                  <span style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 7px',
                    borderRadius: 'var(--radius-sm)',
                    ...priorityBadgeStyle(issue.priority),
                  }}>
                    {issue.priority}
                  </span>
                )}

                {/* Story points */}
                {issue.storyPoints != null && (
                  <span style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    minWidth: 36,
                    textAlign: 'right',
                  }}>
                    {issue.storyPoints}pts
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Error */}
      {error && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', marginBottom: 10 }}>
          {error}
        </p>
      )}

      {/* Action row */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => void handleStart()}
          disabled={!selectedId || loading}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 600,
            padding: '9px 20px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: selectedId ? 'var(--primary)' : 'rgba(124,58,237,0.3)',
            color: 'white',
            cursor: selectedId && !loading ? 'pointer' : 'not-allowed',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.15s',
          }}
        >
          {loading ? 'Starting…' : 'Start Working on This Issue →'}
        </button>
      </div>

    </div>
  );
}
